import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Modal, Form, Input, Select, Button } from 'antd';
import {
  updateKnowledgebaseOverridesAction,
  getKnowledgebaseOverridesAction,
  listKnowledgebaseOverridesAction,
} from '../../../../../store/actions/knowledgebaseoverrides';

const { Option } = Select;
const { TextArea } = Input;

// Constants for select options based on the model definition
const ENCOUNTER_PHASES = ['pre-chart', 'in-visit', 'after-visit', 'dictate'];
const ENCOUNTER_TYPES = [
  'intake',
  'followup',
  'ambient',
  'note',
  'icd-10',
  'cpt',
];

const EditKnowledgeBaseOverrides = ({
  isOpen,
  setIsOpen,
  knowledgebaseId,
  isfilterOn,
  filterValue,
  orgUuid,
}) => {
  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const [contentText, setContentText] = useState('');
  const { knowledgebaseOverridesItem } = useSelector(
    (state) => state?.knowledgebaseState
  );
  const { specialties } = useSelector((state) => ({
    specialties: state?.specialtyState?.getAllSpecialtiesList,
  }));

  useEffect(() => {
    if (isOpen && knowledgebaseId && orgUuid) {
      dispatch(getKnowledgebaseOverridesAction(orgUuid, knowledgebaseId));
    }
  }, [dispatch, isOpen, knowledgebaseId, orgUuid]);

  useEffect(() => {
    if (knowledgebaseOverridesItem && isOpen) {
      form.setFieldsValue({
        specialty_id: knowledgebaseOverridesItem.specialty_id,
        content: knowledgebaseOverridesItem.content,
        encounter_phase: knowledgebaseOverridesItem.encounter_phase,
        encounter_type: knowledgebaseOverridesItem.encounter_type,
      });

      setContentText(knowledgebaseOverridesItem.content);
    }
  }, [knowledgebaseOverridesItem, isOpen, form]);

  const handleClose = () => {
    form.resetFields();
    setContentText('');
    setIsOpen(false);
  };

  const handleSubmit = (values) => {
    if (!orgUuid) {
      console.error('Organization UUID is required');
      return;
    }

    const payload = {
      specialty_id: values.specialty_id || null,
      content: contentText.trim(),
      encounter_phase: values.encounter_phase,
      encounter_type: values.encounter_type,
    };

    dispatch(
      updateKnowledgebaseOverridesAction(
        orgUuid,
        knowledgebaseId,
        payload,
        () => {
          handleClose();
          dispatch(listKnowledgebaseOverridesAction(orgUuid, filterValue));
        }
      )
    );
  };

  return (
    <Modal
      title="Edit Knowledge Base Override"
      open={isOpen}
      onCancel={handleClose}
      footer={null}
      width={1000}
    >
      <Form form={form} layout="vertical" onFinish={handleSubmit}>
        <Form.Item name="specialty_id" label="Specialty">
          <Select
            placeholder="Select specialty"
            allowClear
            options={specialties?.map((specialty) => ({
              value: specialty.specialty_id,
              label: specialty.specialty_name,
            }))}
          />
        </Form.Item>

        <Form.Item
          name="encounter_phase"
          label="Encounter Phase"
          rules={[
            { required: true, message: 'Please select an encounter phase' },
          ]}
        >
          <Select placeholder="Select encounter phase" allowClear>
            {ENCOUNTER_PHASES.map((phase) => (
              <Option key={phase} value={phase}>
                {phase}
              </Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item
          name="encounter_type"
          label="Encounter Type"
          rules={[
            { required: true, message: 'Please select an encounter type' },
          ]}
        >
          <Select placeholder="Select encounter type" allowClear>
            {ENCOUNTER_TYPES.map((type) => (
              <Option key={type} value={type}>
                {type}
              </Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item
          name="content"
          label="Content"
          rules={[{ required: true, message: 'Please enter content' }]}
        >
          <TextArea
            value={contentText}
            onChange={(e) => setContentText(e.target.value)}
            placeholder="Enter the knowledge base content here"
            autoSize={{ minRows: 5, maxRows: 15 }}
            className="w-full"
          />
        </Form.Item>

        <div className="flex justify-end">
          <Button onClick={handleClose} className="mr-2">
            Cancel
          </Button>
          <Button type="primary" htmlType="submit" disabled={!orgUuid}>
            Save Changes
          </Button>
        </div>
      </Form>
    </Modal>
  );
};

export default EditKnowledgeBaseOverrides;
